import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) {
  throw new Error('VITE_API_URL is not set. Please set it in your environment variables.');
}

export interface DetectionResult {
  disease: string;
  confidence: number;
  description: string;
  treatment: string[];
  preventionMeasures: string[];
  imageUrl?: string;
}

// Comprehensive list of plant diseases and pests
const DISEASES = [
  {
    id: 'late_blight',
    disease: 'Late Blight',
    confidence: 0.92,
    description: 'Late blight is a plant disease caused by the oomycete pathogen Phytophthora infestans. It primarily affects potatoes and tomatoes, causing significant crop losses worldwide. Symptoms include dark lesions on leaves and stems, often with a wet appearance.',
    treatment: [
      'Apply fungicides containing chlorothalonil, mancozeb, or copper compounds',
      'Remove and destroy infected plant parts',
      'Increase plant spacing to improve air circulation'
    ],
    preventionMeasures: [
      'Use resistant varieties',
      'Practice crop rotation',
      'Avoid overhead irrigation',
      'Plant in well-drained soil'
    ]
  },
  {
    id: 'powdery_mildew',
    disease: 'Powdery Mildew',
    confidence: 0.88,
    description: 'Powdery mildew is a fungal disease that affects a wide range of plants. It appears as a white to gray powdery growth on leaf surfaces, stems, and sometimes fruit. Common in squash, cucumber, and many ornamental plants.',
    treatment: [
      'Apply sulfur-based fungicides',
      'Use neem oil or potassium bicarbonate sprays',
      'Prune affected areas to increase airflow'
    ],
    preventionMeasures: [
      'Space plants properly for good air circulation',
      'Avoid excessive nitrogen fertilization',
      'Water at the base of plants, not on foliage',
      'Clean garden tools regularly'
    ]
  },
  {
    id: 'aphid_infestation',
    keywords: ['aphid', 'insect', 'sticky', 'honeydew', 'small', 'green', 'cluster', 'curl'],
    disease: 'Aphid Infestation',
    confidence: 0.85,
    description: 'Aphids are small sap-sucking insects that can cause stunted growth, leaf curl, and transmit plant viruses. They often cluster on new growth and the undersides of leaves, leaving behind sticky honeydew.',
    treatment: [
      'Spray with insecticidal soap or neem oil',
      'Introduce natural predators like ladybugs',
      'Use a strong stream of water to knock aphids off plants'
    ],
    preventionMeasures: [
      'Monitor plants regularly for early detection',
      'Use reflective mulch to repel aphids',
      'Plant trap crops or repellent plants like marigolds',
      'Maintain healthy plants with proper nutrition'
    ]
  },
  {
    id: 'downy_mildew',
    keywords: ['downy', 'mildew', 'yellow', 'spots', 'grape', 'cucumber', 'humid'],
    disease: 'Downy Mildew',
    confidence: 0.87,
    description: 'Downy mildew causes yellow or pale green spots on the upper surfaces of older leaves and fuzzy white to grayish growth on the undersides. Common in grapes, cucumbers, and other crops, especially in humid conditions.',
    treatment: [
      'Apply copper-based fungicides',
      'Use phosphorous acid or potassium phosphite products',
      'Remove and destroy infected plant material'
    ],
    preventionMeasures: [
      'Improve air circulation around plants',
      'Avoid wetting leaves when watering',
      'Plant resistant varieties',
      'Use proper plant spacing'
    ]
  },
  {
    id: 'early_blight',
    keywords: ['early', 'blight', 'tomato', 'potato', 'circular', 'targets', 'brown', 'spots'],
    disease: 'Early Blight',
    confidence: 0.89,
    description: 'Early blight is caused by the fungus Alternaria solani and affects tomatoes and potatoes. It appears as dark brown spots with concentric rings (target-like appearance) primarily on lower leaves first.',
    treatment: [
      'Apply fungicides with chlorothalonil or copper',
      'Remove infected leaves immediately',
      'Stake plants to keep foliage off the ground'
    ],
    preventionMeasures: [
      'Practice crop rotation (3-4 years)',
      'Use resistant varieties',
      'Mulch around plants',
      'Provide adequate plant nutrition'
    ]
  },
  {
    id: 'bacterial_spot',
    keywords: ['bacterial', 'spot', 'pepper', 'tomato', 'water', 'soaked', 'black'],
    disease: 'Bacterial Spot',
    confidence: 0.84,
    description: 'Bacterial spot affects peppers and tomatoes, causing small, dark, water-soaked, circular spots on leaves, stems, and fruits. In severe cases, defoliation can occur, exposing fruits to sunscald.',
    treatment: [
      'Apply copper-based bactericides',
      'Remove infected plants or plant parts',
      'Avoid working in fields when plants are wet'
    ],
    preventionMeasures: [
      'Use disease-free seeds and transplants',
      'Practice crop rotation',
      'Avoid overhead irrigation',
      'Space plants for good air circulation'
    ]
  },
  {
    id: 'anthracnose',
    keywords: ['anthracnose', 'sunken', 'lesion', 'bean', 'pepper', 'tomato', 'cucumber', 'dark'],
    disease: 'Anthracnose',
    confidence: 0.86,
    description: 'Anthracnose is a fungal disease that affects many crops including beans, peppers, tomatoes, and cucumbers. It causes dark, sunken lesions on fruits, leaves, and stems, often with pinkish spore masses in the center.',
    treatment: [
      'Apply fungicides containing chlorothalonil or mancozeb',
      'Remove and destroy infected plant parts',
      'Adjust watering practices to keep foliage dry'
    ],
    preventionMeasures: [
      'Use disease-free seeds',
      'Practice crop rotation',
      'Provide good air circulation',
      'Apply mulch to prevent soil splash'
    ]
  },
  {
    id: 'fusarium_wilt',
    keywords: ['fusarium', 'wilt', 'yellowing', 'tomato', 'banana', 'wilting', 'vessel'],
    disease: 'Fusarium Wilt',
    confidence: 0.91,
    description: 'Fusarium wilt is a soil-borne fungal disease that affects many crops including tomatoes, bananas, and cotton. It causes yellowing and wilting of leaves, often on one side of the plant, and discoloration of vascular tissue.',
    treatment: [
      'No effective chemical treatments once infected',
      'Remove and destroy infected plants',
      'Sterilize tools after use on infected plants'
    ],
    preventionMeasures: [
      'Plant resistant varieties',
      'Practice long-term crop rotation',
      'Maintain optimal soil pH (6.5-7)',
      'Use raised beds in poorly drained areas'
    ]
  },
  {
    id: 'corn_rust',
    keywords: ['corn', 'rust', 'maize', 'orange', 'pustules', 'rusty'],
    disease: 'Corn Rust',
    confidence: 0.88,
    description: 'Corn rust is caused by fungal pathogens that create reddish-orange or brown pustules on corn leaves. In severe cases, it can significantly reduce yield by decreasing photosynthetic area.',
    treatment: [
      'Apply foliar fungicides if detected early',
      'Time applications according to disease progression',
      'Follow local extension service recommendations'
    ],
    preventionMeasures: [
      'Plant resistant hybrids',
      'Plant early to avoid peak spore periods',
      'Monitor fields regularly',
      'Maintain proper plant nutrition'
    ]
  },
  {
    id: 'fall_armyworm',
    keywords: ['armyworm', 'worm', 'caterpillar', 'corn', 'maize', 'feeding', 'damage'],
    disease: 'Fall Armyworm Infestation',
    confidence: 0.87,
    description: 'Fall armyworms are caterpillars that cause severe defoliation in many crops, especially corn and other grasses. They feed on leaves, creating ragged edges and large holes, and can destroy entire fields if not controlled.',
    treatment: [
      'Apply appropriate insecticides (consult local extension service)',
      'Use biological controls like Bacillus thuringiensis (Bt)',
      'Release natural predators if available'
    ],
    preventionMeasures: [
      'Plant early to avoid peak infestation periods',
      'Use pheromone traps for monitoring',
      'Plant Bt corn varieties where approved',
      'Implement push-pull strategies with trap crops'
    ]
  }
];

export const pestDetectionService = {
  async detectDisease(imageFile: File): Promise<DetectionResult> {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await axios.post(`${API_URL}/pest-detection/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error detecting disease:', error);
      throw error;
    }
  },

  async getDiseaseInfo(diseaseName: string): Promise<DetectionResult> {
    try {
      const response = await axios.get(`${API_URL}/pest-detection/disease/${diseaseName}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching disease info for ${diseaseName}:`, error);
      throw error;
    }
  },

  // Function to simulate image analysis
  getMockDetectionResult(imageFile: File): Promise<DetectionResult> {
    return new Promise((resolve) => {
      // Create a placeholder for the image reader result
      let imageContent = '';
      
      // Create a FileReader to get image data
      const reader = new FileReader();
      
      reader.onload = function(event) {
        if (event.target && event.target.result) {
          // Get the image data
          imageContent = event.target.result.toString();
          
          // In a real system, we would analyze the image using ML
          // For this simulation, we'll create a hash of the image data
          // to ensure consistent results for the same image
          const hash = simpleHash(imageContent);
          
          // Use the hash to determine which disease to return (deterministic)
          const diseaseIndex = Math.abs(hash) % DISEASES.length;
          const disease = DISEASES[diseaseIndex];
          
          // Add some randomness to confidence but keep it high (90-98%)
          const confidenceVariation = (hash % 100) / 1000; // Small variation
          const confidence = Math.min(0.98, disease.confidence + confidenceVariation);
          
          // Resolve with the disease data
          setTimeout(() => {
            resolve({
              ...disease,
              confidence: confidence,
            });
          }, 1500); // Simulate analysis time
        }
      };
      
      // Start reading the image file
      reader.readAsDataURL(imageFile);
    });
  }
};

// Simple hash function to create a numeric hash from string
function simpleHash(str: string): number {
  // Only use the first 1000 chars to avoid performance issues with large images
  const sampleStr = str.substring(0, 1000);
  let hash = 0;
  for (let i = 0; i < sampleStr.length; i++) {
    const char = sampleStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}